import React from 'react';
import axios from 'axios';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Divider,
  Progress,
  Radio,
  Space,
  Steps,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ForwardOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import BasicSystemSetupPanel from './BasicSystemSetupPanel';
import DataUploadForm from './DataUploadForm';
import * as serverconfig from '../serverconn';
import {
  ONBOARDING_WIZARD_STEPS,
  loadSkippedSteps,
  saveSkippedStep,
} from '../onboardingSteps';
import {
  LOAN_MIGRATION_PATHS,
  LOAN_REPAYMENT_FIELD_GUIDE,
  LOAN_SCHEDULE_FIELD_GUIDE,
  LOAN_TEMPLATE_FIELD_GUIDE,
} from '../loanOnboardingGuide';
import { INSTITUTION_TERMS } from '../institutionCopy';

var CryptoJS = require('crypto-js');

const { Step } = Steps;
const { Title, Paragraph, Text } = Typography;

function getSaccoId() {
  if (!localStorage.getItem('sacco')) {
    return '';
  }
  return CryptoJS.AES.decrypt(localStorage.getItem('sacco'), 'my-secret-key@12345').toString(
    CryptoJS.enc.Utf8
  );
}

class OnboardingWizard extends React.Component {
  state = {
    current: 0,
    progress: null,
    loading: true,
    completing: false,
    selectedUploadType: {},
    uploadResetKeys: {},
  };

  componentDidMount() {
    this.loadProgress();
  }

  loadProgress = () => {
    const saccoId = getSaccoId();
    if (!saccoId) {
      this.setState({ loading: false });
      return;
    }

    const skipped = loadSkippedSteps(saccoId);
    const form_data = new FormData();
    form_data.append('sacco', saccoId);
    form_data.append('skipped_steps', JSON.stringify(skipped));

    this.setState({ loading: true });
    axios
      .post(`${serverconfig.backendserverurl}/customqueries/get_onboarding_progress`, form_data)
      .then((res) => {
        const progress = res.data;
        const recommended = progress.recommended_step_index || 0;
        const wizardIndex = Math.min(
          Math.max(recommended, 0),
          ONBOARDING_WIZARD_STEPS.length - 1
        );
        this.setState({
          progress,
          loading: false,
          current: this.state.current || wizardIndex,
        });
        if (this.props.onProgressChange) {
          this.props.onProgressChange(progress);
        }
      })
      .catch((err) => {
        const msg =
          (err.response && err.response.data && err.response.data.message) ||
          'Could not load onboarding progress.';
        message.error(msg);
        this.setState({ loading: false });
      });
  };

  getProgressForStep = (stepKey) => {
    const { progress } = this.state;
    if (!progress || !progress.steps) {
      return null;
    }
    return progress.steps.find((s) => s.key === stepKey) || null;
  };

  handleSkipOptional = () => {
    const step = ONBOARDING_WIZARD_STEPS[this.state.current];
    if (!step || !step.optional || !step.progressKey) {
      return;
    }
    const saccoId = getSaccoId();
    saveSkippedStep(saccoId, step.progressKey);
    message.info(`Skipped "${step.title}" for now. You can return later from this wizard.`);
    this.loadProgress();
    this.goNext();
  };

  goNext = () => {
    this.setState((prev) => ({
      current: Math.min(prev.current + 1, ONBOARDING_WIZARD_STEPS.length - 1),
    }));
  };

  goBack = () => {
    this.setState((prev) => ({
      current: Math.max(prev.current - 1, 0),
    }));
  };

  goToStep = (index) => {
    this.setState({ current: index });
  };

  handleUploadSuccess = (stepKey) => {
    message.success('Upload submitted. Refreshing progress…');
    setTimeout(() => this.loadProgress(), 1500);
  };

  bumpUploadReset = (stepKey) => {
    this.setState((prev) => ({
      uploadResetKeys: {
        ...prev.uploadResetKeys,
        [stepKey]: (prev.uploadResetKeys[stepKey] || 0) + 1,
      },
    }));
  };

  handleMarkValidated = () => {
    const saccoId = getSaccoId();
    saveSkippedStep(saccoId, 'validate');
    message.success('Validation step noted. You can still review reports anytime.');
    this.loadProgress();
    this.goNext();
  };

  handleCompleteOnboarding = () => {
    const saccoId = getSaccoId();
    if (!saccoId) {
      message.error(INSTITUTION_TERMS.noOrganizationSelected);
      return;
    }

    this.setState({ completing: true });
    const form_data = new FormData();
    form_data.append('sacco', saccoId);

    axios
      .post(`${serverconfig.backendserverurl}/customqueries/mark_onboarding_complete`, form_data)
      .then((res) => {
        message.success(res.data.message);
        this.setState({ completing: false, progress: res.data.progress });
        if (this.props.onProgressChange) {
          this.props.onProgressChange(res.data.progress);
        }
        this.loadProgress();
      })
      .catch((err) => {
        const msg =
          (err.response && err.response.data && err.response.data.message) ||
          'Could not finish onboarding.';
        message.error(msg);
        this.setState({ completing: false });
      });
  };

  handleReopenOnboarding = () => {
    const saccoId = getSaccoId();
    const form_data = new FormData();
    form_data.append('sacco', saccoId);

    axios
      .post(`${serverconfig.backendserverurl}/customqueries/reopen_onboarding`, form_data)
      .then((res) => {
        message.success(res.data.message);
        this.loadProgress();
      })
      .catch((err) => {
        message.error(
          (err.response && err.response.data && err.response.data.message) ||
            'Could not reopen onboarding.'
        );
      });
  };

  renderStepStatusTag(stepKey) {
    const info = this.getProgressForStep(stepKey);
    if (!info) {
      return null;
    }
    const colors = {
      done: 'success',
      skipped: 'default',
      optional: 'processing',
      pending: 'warning',
    };
    return (
      <Tag color={colors[info.status] || 'default'} style={{ marginLeft: 8 }}>
        {info.status}
        {info.count !== undefined ? ` (${info.count})` : ''}
      </Tag>
    );
  }

  renderLoanGuidePanel() {
    const snapshot = LOAN_MIGRATION_PATHS.find((p) => p.id === 'snapshot');
    return (
      <div style={{ marginBottom: 24 }}>
        <Alert
          type="success"
          showIcon
          message={snapshot.title}
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {snapshot.steps.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 16 }}
        />
        <Collapse ghost defaultActiveKey={['paths']}>
          <Collapse.Panel header="All loan migration paths (A / B / C)" key="paths">
            {LOAN_MIGRATION_PATHS.map((path) => (
              <Card key={path.id} size="small" style={{ marginBottom: 12 }} title={path.title}>
                <Paragraph type="secondary">{path.when}</Paragraph>
                <Text strong>Steps</Text>
                <ul>
                  {path.steps.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <Text type="danger">{path.avoid}</Text>
              </Card>
            ))}
          </Collapse.Panel>
          <Collapse.Panel header="Template columns → database mapping" key="fields">
            <ul style={{ fontSize: 13 }}>
              {LOAN_TEMPLATE_FIELD_GUIDE.map((row) => (
                <li key={row.column}>
                  <Text strong>{row.column}</Text> → {row.mapsTo}: {row.meaning}
                </li>
              ))}
            </ul>
          </Collapse.Panel>
        </Collapse>
      </div>
    );
  }

  renderLoanRepaymentsGuide() {
    return (
      <Alert
        type="warning"
        showIcon
        message="Path B only — skip this step for partially paid loans (Path A)"
        description={
          <div>
            <p>
              If you already set <strong>Principle balance</strong> and <strong>Interest balance</strong> on
              the running loans import, the system recorded synthetic opening repayments. Importing
              repayments here would double-count.
            </p>
            <ul>
              {LOAN_REPAYMENT_FIELD_GUIDE.map((row) => (
                <li key={row.column}>
                  <Text strong>{row.column}</Text>: {row.meaning}
                </li>
              ))}
            </ul>
          </div>
        }
        style={{ marginBottom: 16 }}
      />
    );
  }

  renderUploadStep(step) {
    const selected =
      this.state.selectedUploadType[step.key] || (step.uploadTypes && step.uploadTypes[0]?.value);
    const resetKey = this.state.uploadResetKeys[step.key] || 0;

    return (
      <div>
        {step.key === 'loan_repayments' && this.renderLoanRepaymentsGuide()}
        {step.key === 'loan_schedules' && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Import your old amortization table"
            description={(
              <ul style={{ marginBottom: 0 }}>
                {LOAN_SCHEDULE_FIELD_GUIDE.map((row) => (
                  <li key={row.column}>
                    <Text strong>{row.column}</Text>: {row.meaning}
                  </li>
                ))}
              </ul>
            )}
          />
        )}
        <Paragraph>{step.tip}</Paragraph>
        {step.key === 'opening_balances' && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Choose the right opening-balance upload"
            description={(
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li><Text strong>Opening deposits</Text> — member savings, shares, and reserve</li>
                <li><Text strong>COA / trial balance opening balances</Text> — other GL accounts (equipment, payables, equity, etc.)</li>
                <li><Text strong>Multi-transaction opening balances</Text> — per-member savings/shares via multi-transaction format</li>
              </ul>
            )}
          />
        )}
        {step.uploadTypes && step.uploadTypes.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Import type</Text>
            <Radio.Group
              value={selected}
              onChange={(e) =>
                this.setState({
                  selectedUploadType: { ...this.state.selectedUploadType, [step.key]: e.target.value },
                })
              }
            >
              <Space direction="vertical">
                {step.uploadTypes.map((item) => (
                  <Radio key={item.value} value={item.value}>
                    {item.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </div>
        )}
        {step.uploadTypesExtra && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Optional extras"
            description={
              <ul style={{ marginBottom: 0 }}>
                {step.uploadTypesExtra.map((item) => (
                  <li key={item.value}>{item.label}</li>
                ))}
              </ul>
            }
          />
        )}
        {step.extraLinks && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Related tools"
            description={
              <ul style={{ marginBottom: 0 }}>
                {step.extraLinks.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            }
          />
        )}
        {selected && (
          <DataUploadForm
            key={`${step.key}-${selected}-${resetKey}`}
            fixedUploadType={selected}
            hideTypeSelector
            compact
            showReconcileLoans={['loans', 'loan_schedules', 'loan_repayments'].includes(step.key)}
            onUploadSuccess={() => {
              this.handleUploadSuccess(step.key);
              this.bumpUploadReset(step.key);
            }}
          />
        )}
      </div>
    );
  }

  renderStepContent() {
    const step = ONBOARDING_WIZARD_STEPS[this.state.current];
    if (!step) {
      return null;
    }

    switch (step.type) {
      case 'welcome':
        return (
          <div>
            <Title level={4}>Welcome to {INSTITUTION_TERMS.dataOnboarding.toLowerCase()}</Title>
            <Paragraph>
              This guided wizard walks you through migrating from your legacy system into Pitech.
              Most steps use Excel templates — download, fill in your data, and upload.
            </Paragraph>
            <Alert
              type="info"
              showIcon
              message="What you will need"
              description={
                <ul>
                  <li>Customer account list with account IDs and types</li>
                  <li>Opening deposit and share balances (if any)</li>
                  <li>Other GL / trial balance opening balances (equipment, payables, equity)</li>
                  <li>Outstanding loans and repayments (if any)</li>
                  <li>Historical expenses and incomes (optional)</li>
                  <li>At least one staff user who can operate the system</li>
                </ul>
              }
            />
            <Divider />
            <Paragraph type="secondary">
              Large imports run in the background. You can continue to the next step while they
              process. Refresh progress anytime with the button in the header.
            </Paragraph>
          </div>
        );

      case 'setup':
        return (
          <div>
            <Paragraph>
              This runs automatically when you open this step. It creates the full chart of accounts,
              wires every company profile and loan setting, and prepares reports — without changing
              data you already have.
            </Paragraph>
            <BasicSystemSetupPanel
              showChecklistExpanded
              autoRunOnMount
              onSetupComplete={this.loadProgress}
            />
          </div>
        );

      case 'upload':
        return this.renderUploadStep(step);

      case 'loans':
        return (
          <div>
            {this.renderLoanGuidePanel()}
            {this.renderUploadStep(step)}
          </div>
        );

      case 'advanced':
        return (
          <div>
            <Paragraph>{step.tip}</Paragraph>
            <Alert
              type="info"
              showIcon
              message="When to use advanced tools"
              description={(
                <ul>
                  <li>Customer-specific journal or statement lines not covered by Excel bulk templates</li>
                  <li>Full loan workflow JSON (applications, assessments, schedules) for one account</li>
                  <li>Correcting loan balances after a bulk import (Path C)</li>
                </ul>
              )}
              style={{ marginBottom: 16 }}
            />
            <Space direction="vertical">
              {(step.links || []).map((link) => (
                <Link key={link.to} to={link.to}>
                  <Button type="primary" ghost>{link.label}</Button>
                </Link>
              ))}
            </Space>
          </div>
        );

      case 'users':
        return (
          <div>
            <Paragraph>
              Import staff via Excel or create accounts manually. At least one active user besides
              yourself is recommended before going live.
            </Paragraph>
            {step.manualLink && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={
                  <span>
                    <Link to={step.manualLink.to}>{step.manualLink.label}</Link>
                  </span>
                }
              />
            )}
            {this.renderUploadStep(step)}
          </div>
        );

      case 'validate':
        return (
          <div>
            <Paragraph>
              Open these reports and confirm balances look correct from your onboarding date
              forward. Fix any issues before marking onboarding complete.
            </Paragraph>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {(step.reportLinks || []).map((link) => (
                <Card key={link.to} size="small">
                  <Link to={link.to} target="_blank">
                    {link.label}
                  </Link>
                </Card>
              ))}
            </Space>
            <Divider />
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={this.handleMarkValidated}>
              I have reviewed the reports — continue
            </Button>
          </div>
        );

      case 'complete': {
        const { progress } = this.state;
        const done = progress && progress.client_onboarding_done;

        return (
          <div>
            {done ? (
              <Alert
                type="success"
                showIcon
                message="Onboarding complete"
                description={`${INSTITUTION_TERMS.goLive}. Onboarding-only upload types are hidden; operational imports remain available under Data Uploads in Settings.`}
                style={{ marginBottom: 16 }}
              />
            ) : (
              <Alert
                type={progress && progress.can_complete ? 'success' : 'warning'}
                showIcon
                message={
                  progress && progress.can_complete
                    ? 'Ready to go live'
                    : 'Complete required steps first'
                }
                description={
                  progress && progress.can_complete
                    ? 'Finishing will mark onboarding complete and streamline future data uploads.'
                    : 'Run system setup and import at least one customer account. Optional steps can be skipped.'
                }
                style={{ marginBottom: 16 }}
              />
            )}
            <Space>
              {!done && (
                <Button
                  type="primary"
                  size="large"
                  icon={<RocketOutlined />}
                  loading={this.state.completing}
                  disabled={!(progress && progress.can_complete)}
                  onClick={this.handleCompleteOnboarding}
                >
                  Finish onboarding & go live
                </Button>
              )}
              {done && (
                <Button onClick={this.handleReopenOnboarding}>Reopen guided onboarding</Button>
              )}
            </Space>
          </div>
        );
      }

      default:
        return null;
    }
  }

  render() {
    const { progress, loading, current } = this.state;
    const step = ONBOARDING_WIZARD_STEPS[current];
    const isOptional = step && step.optional;
    const isLast = current === ONBOARDING_WIZARD_STEPS.length - 1;

    if (loading && !progress) {
      return <Card loading style={{ minHeight: 200 }} />;
    }

    return (
      <div>
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <Title level={4} style={{ marginBottom: 4 }}>
                {INSTITUTION_TERMS.onboardingWizard}
              </Title>
              <Text type="secondary">
                {progress && progress.client_onboarding_done
                  ? 'Onboarding complete'
                  : 'Follow each step — skip optional sections if not needed'}
              </Text>
            </div>
            <div style={{ minWidth: 200, flex: 1 }}>
              <Progress
                percent={progress ? progress.progress_percent : 0}
                status={progress && progress.client_onboarding_done ? 'success' : 'active'}
              />
            </div>
            <Button onClick={this.loadProgress}>Refresh progress</Button>
          </div>
        </Card>

        <Steps
          current={current}
          onChange={this.goToStep}
          size="small"
          style={{ marginBottom: 24, overflowX: 'auto' }}
        >
          {ONBOARDING_WIZARD_STEPS.map((s) => {
            const pinfo = s.progressKey ? this.getProgressForStep(s.progressKey) : null;
            let stepStatus = undefined;
            if (pinfo) {
              if (pinfo.status === 'done' || pinfo.status === 'skipped') {
                stepStatus = 'finish';
              } else if (pinfo.status === 'optional') {
                stepStatus = 'wait';
              }
            }
            return (
              <Step
                key={s.key}
                title={s.title}
                description={s.subtitle}
                status={stepStatus}
              />
            );
          })}
        </Steps>

        <Card
          title={
            <span>
              {step && step.title}
              {step && step.progressKey && this.renderStepStatusTag(step.progressKey)}
              {isOptional && (
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  optional
                </Tag>
              )}
            </span>
          }
        >
          {this.renderStepContent()}

          <Divider />

          <Space wrap>
            <Button
              icon={<ArrowLeftOutlined />}
              disabled={current === 0}
              onClick={this.goBack}
            >
              Back
            </Button>
            {!isLast && (
              <Button type="primary" icon={<ArrowRightOutlined />} onClick={this.goNext}>
                Next
              </Button>
            )}
            {isOptional && (
              <Button icon={<ForwardOutlined />} onClick={this.handleSkipOptional}>
                Skip for now
              </Button>
            )}
          </Space>
        </Card>
      </div>
    );
  }
}

export default OnboardingWizard;
